<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";

const Dialog = defineAsyncComponent(() => import("primevue/dialog"));

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
        required: true,
    },
    // Each entry: { label, demo } where `demo` matches the WrGenicLiveDemo shape.
    prompts: {
        type: Array,
        default: () => [
            {
                label: "Show me my cart",
                demo: {
                    userMessage: "Show me my cart.",
                    agentMessage:
                        "Your cart is ready — 3 items, $248.00 total. Ready to check out?",
                    chip: "#cart-2048",
                    panelTitle: "Shopping Cart",
                    panelPill: "3 ITEMS",
                    panel: {
                        type: "table",
                        head: ["Product", "Price", "Stock"],
                        rows: [
                            {
                                name: "Aurora Desk Lamp",
                                role: "Lighting · ×1",
                                tag: "$86.00",
                                initials: "AD",
                                color: "linear-gradient(135deg, #4b4fd6, #262b7e)",
                                status: "stable",
                                statusLabel: "In stock",
                            },
                            {
                                name: "Nimbus Keyboard",
                                role: "Peripherals · ×1",
                                tag: "$112.00",
                                initials: "NK",
                                color: "linear-gradient(135deg, #8b5cf6, #5b21b6)",
                                status: "stable",
                                statusLabel: "In stock",
                            },
                            {
                                name: "Terra Ceramic Mug",
                                role: "Kitchen · ×1",
                                tag: "$50.00",
                                initials: "TM",
                                color: "linear-gradient(135deg, #0e7490, #164e63)",
                                status: "audit",
                                statusLabel: "Low stock",
                            },
                        ],
                    },
                    applyText: "Subtotal $248.00 · Free shipping",
                    applyLabel: "Proceed to Checkout",
                },
            },
            {
                label: "Show me sales report",
                demo: {
                    userMessage: "Show me the sales report for this quarter.",
                    agentMessage:
                        "Here's your team's sales performance this quarter — 4 reps, $482K total.",
                    chip: "#sales-q3",
                    panelTitle: "Sales Report",
                    panelPill: "Q3 2026",
                    panel: {
                        type: "table",
                        head: ["Sales Rep", "Revenue", "Trend"],
                        rows: [
                            {
                                name: "Priya Sharma",
                                role: "Enterprise · 11 deals",
                                tag: "$142K",
                                initials: "PS",
                                color: "linear-gradient(135deg, #4b4fd6, #262b7e)",
                                status: "stable",
                                statusLabel: "↑ 12%",
                            },
                            {
                                name: "Marcus Lee",
                                role: "Mid-Market · 9 deals",
                                tag: "$118K",
                                initials: "ML",
                                color: "linear-gradient(135deg, #8b5cf6, #5b21b6)",
                                status: "stable",
                                statusLabel: "↑ 8%",
                            },
                            {
                                name: "Aisha Khan",
                                role: "SMB · 8 deals",
                                tag: "$96K",
                                initials: "AK",
                                color: "linear-gradient(135deg, #0e7490, #164e63)",
                                status: "audit",
                                statusLabel: "↓ 3%",
                            },
                            {
                                name: "Diego Torres",
                                role: "Enterprise · 6 deals",
                                tag: "$126K",
                                initials: "DT",
                                color: "linear-gradient(135deg, #047857, #065f46)",
                                status: "stable",
                                statusLabel: "↑ 15%",
                            },
                        ],
                    },
                    applyText: "34 deals closed · $482K total revenue",
                    applyLabel: "Export Report",
                },
            },
            {
                label: "Revenue, last 7 days",
                demo: {
                    userMessage: "Show my revenue for the last 7 days.",
                    agentMessage:
                        "Revenue is up 12% week over week — $1.56M this quarter, led by enterprise seats.",
                    chip: "#revenue-q3",
                    panelTitle: "Revenue",
                    panelPill: "+12% WoW",
                    panel: {
                        type: "chart",
                        chartLabel: "Revenue",
                        legendA: "This week",
                        legendB: "Last week",
                        axis: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                        holdings: [
                            { name: "Enterprise", ticker: "ENT", allocation: "54%" },
                            { name: "SMB", ticker: "SMB", allocation: "31%" },
                            { name: "API Usage", ticker: "API", allocation: "15%" },
                        ],
                    },
                },
            },
            {
                label: "Show me the weather",
                demo: {
                    userMessage: "Show me the weather at my current location.",
                    agentMessage:
                        "Fetching live weather for your current location.",
                    chip: "#weather-live",
                    panelTitle: "Current Weather",
                    panelPill: "LIVE",
                    panel: {
                        type: "weather",
                    },
                },
            },
        ],
    },
});

const emit = defineEmits(["close"]);
const visible = ref(props.isOpen);

// ── Active prompt (null = nothing selected yet) ─────────────────────
const activeIndex = ref(null);
const currentPrompt = computed(() =>
    activeIndex.value === null ? null : props.prompts[activeIndex.value]
);
const demo = computed(() => currentPrompt.value?.demo ?? {});

// ── Conversation playback state ─────────────────────────────────────
const userStarted = ref(false);
const userText = ref("");
const inputText = ref("");
const typingInput = ref(false);
const sending = ref(false);
const showAgent = ref(false);
const agentThinking = ref(false);
const agentText = ref("");
const typingAgent = ref(false);
const showChips = ref(false);
const showPanel = ref(false);

let timers = [];

function schedule(fn, delay) {
    timers.push(setTimeout(fn, delay));
}

function resetConversation() {
    timers.forEach(clearTimeout);
    timers = [];
    userStarted.value = false;
    userText.value = "";
    inputText.value = "";
    typingInput.value = false;
    sending.value = false;
    showAgent.value = false;
    agentThinking.value = false;
    agentText.value = "";
    typingAgent.value = false;
    showChips.value = false;
    showPanel.value = false;
}

function typeText(text, target, typing, speed, onDone) {
    typing.value = true;
    let i = 0;
    const step = () => {
        i++;
        target.value = text.slice(0, i);
        if (i < text.length) {
            schedule(step, speed);
        } else {
            typing.value = false;
            onDone && onDone();
        }
    };
    step();
}

// ── Live weather (Open-Meteo + geolocation, no API key) ─────────────
const FALLBACK_LOCATION = { lat: 28.6139, lng: 77.209 }; // Delhi, IN
const weather = ref(null);
const weatherLoading = ref(false);
const weatherError = ref(false);
let weatherToken = 0;

function getUserLocation() {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve(FALLBACK_LOCATION);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
            () => resolve(FALLBACK_LOCATION),
            { timeout: 8000, maximumAge: 60000 }
        );
    });
}

function weatherIcon(code) {
    if (code === 0) return "☀️";
    if (code <= 3) return "🌤️";
    if (code === 45 || code === 48) return "🌫️";
    if (code >= 51 && code <= 57) return "🌦️";
    if (code >= 61 && code <= 67) return "🌧️";
    if (code >= 71 && code <= 77) return "🌨️";
    if (code >= 80 && code <= 82) return "🌧️";
    if (code >= 95) return "⛈️";
    return "☁️";
}

function weatherCondition(code) {
    if (code === 0) return "Clear sky";
    if (code === 1) return "Mainly clear";
    if (code === 2) return "Partly cloudy";
    if (code === 3) return "Overcast";
    if (code === 45 || code === 48) return "Foggy";
    if (code >= 51 && code <= 57) return "Drizzle";
    if (code >= 61 && code <= 65) return "Rain";
    if (code === 66 || code === 67) return "Heavy rain";
    if (code >= 71 && code <= 75) return "Snow";
    if (code === 77) return "Snow grains";
    if (code >= 80 && code <= 82) return "Rain showers";
    if (code >= 95) return "Thunderstorm";
    return "Cloudy";
}

async function fetchWeather() {
    const token = ++weatherToken;
    weatherLoading.value = true;
    weatherError.value = false;

    try {
        const pos = await getUserLocation();
        if (token !== weatherToken) return;

        // Reverse-geocode for a friendly place name (best effort).
        let location = "Your location";
        try {
            const rg = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${pos.lat}&lon=${pos.lng}&zoom=10&accept-language=en`
            );
            const rd = await rg.json();
            if (rd) {
                const a = rd.address || {};
                location =
                    a.city || a.town || a.village || a.municipality || a.state || rd.name || "Your location";
            }
        } catch (e) {
            /* keep default */
        }
        if (token !== weatherToken) return;

        const res = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${pos.lat}&longitude=${pos.lng}` +
                `&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m` +
                `&daily=temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=1`
        );
        const data = await res.json();
        if (token !== weatherToken) return;

        const c = data.current;
        const d = data.daily;
        weather.value = {
            location,
            temp: Math.round(c.temperature_2m),
            feelsLike: Math.round(c.apparent_temperature),
            humidity: c.relative_humidity_2m,
            wind: Math.round(c.wind_speed_10m),
            high: Math.round(d.temperature_2m_max[0]),
            low: Math.round(d.temperature_2m_min[0]),
            condition: weatherCondition(c.weather_code),
            icon: weatherIcon(c.weather_code),
        };
        weatherLoading.value = false;
    } catch (e) {
        if (token === weatherToken) {
            weatherError.value = true;
            weatherLoading.value = false;
        }
    }
}

// ── Playback ─────────────────────────────────────────────────────────
function playPrompt() {
    resetConversation();
    const d = demo.value;

    // Reduced motion: show the full conversation instantly
    if (
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
        userStarted.value = true;
        userText.value = d.userMessage;
        showAgent.value = true;
        agentText.value = d.agentMessage;
        showChips.value = true;
        showPanel.value = true;
        return;
    }

    // 1. Prompt is typed into the input bar
    schedule(() => {
        typeText(d.userMessage, inputText, typingInput, 32, () => {
            // 2. Send button press
            schedule(() => {
                sending.value = true;
                schedule(() => {
                    // 3. Message lands in the chat, input clears
                    sending.value = false;
                    inputText.value = "";
                    userStarted.value = true;
                    userText.value = d.userMessage;
                    // 4. Agent thinks, then streams the reply
                    schedule(() => {
                        showAgent.value = true;
                        agentThinking.value = true;
                        schedule(() => {
                            agentThinking.value = false;
                            typeText(d.agentMessage, agentText, typingAgent, 14, () => {
                                // 5. Chips, then the workspace panel
                                schedule(() => {
                                    showChips.value = true;
                                }, 250);
                                schedule(() => {
                                    showPanel.value = true;
                                }, 500);
                            });
                        }, 900);
                    }, 500);
                }, 250);
            }, 300);
        });
    }, 400);
}

// Clicking a prompt stops any in-flight conversation and starts the new one.
function onPromptClick(index) {
    activeIndex.value = index;
    playPrompt();
    // Kick off the live weather fetch early so data is ready by the panel slide-in.
    if (props.prompts[index]?.demo?.panel?.type === "weather") {
        weather.value = null;
        weatherError.value = false;
        weatherLoading.value = true;
        nextTick(() => fetchWeather());
    }
}

// ── Modal open/close ─────────────────────────────────────────────────
function handleClose() {
    visible.value = false;
}

watch(
    () => props.isOpen,
    (open) => {
        visible.value = open;
        if (open) {
            activeIndex.value = null; // start with nothing selected
            resetConversation();
        } else {
            resetConversation();
        }
    }
);

watch(visible, (newVal) => {
    if (!newVal) {
        emit("close");
    }
});

onMounted(() => {
    if (props.isOpen) {
        activeIndex.value = null;
    }
});

onUnmounted(() => {
    timers.forEach(clearTimeout);
});

// ── Panel color classes ──────────────────────────────────────────────
const statusColorClasses = {
    stable: "text-[#047857] dark:text-[#34d399]",
    audit: "text-[#b45309] dark:text-[#fcd34d]",
    active: "text-[#047857] dark:text-[#34d399]",
};

// ── Chart interactivity (x/y map to the 320x96 SVG viewBox) ─────────
const chartPoints = [
    { x: 0, y: 84, value: "$1.12M", label: "W1" },
    { x: 46, y: 73, value: "$1.18M", label: "W2" },
    { x: 92, y: 77, value: "$1.16M", label: "W3" },
    { x: 138, y: 56, value: "$1.28M", label: "W4" },
    { x: 184, y: 51, value: "$1.31M", label: "W5" },
    { x: 230, y: 31, value: "$1.42M", label: "W6" },
    { x: 276, y: 22, value: "$1.48M", label: "W7" },
    { x: 320, y: 7, value: "$1.56M", label: "W8" },
];

const hoverIndex = ref(null);
const hoverPoint = computed(() =>
    hoverIndex.value === null ? null : chartPoints[hoverIndex.value]
);

function onChartMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const index = Math.round(ratio * (chartPoints.length - 1));
    hoverIndex.value = Math.min(chartPoints.length - 1, Math.max(0, index));
}

function onChartLeave() {
    hoverIndex.value = null;
}
</script>

<template>
    <div class="card flex justify-center z-30">
        <Dialog aria-label="Genic UI Live Demo" @hide="handleClose" v-model:visible="visible"
            :modal="true" :dismissableMask="true" closeOnEscape closable unstyled
            class="w-[94vw] md:w-[88vw] max-w-[1180px]"
            :breakpoints="{ '1199px': '90vw', '575px': '95vw' }"
            :pt="{
                root: { class: 'relative' },
                headerActions: { class: 'hidden' },
                mask: { class: 'backdrop-blur-sm' },
                content: { class: 'max-h-[95vh] overflow-y-auto p-0' },
            }"
            role="dialog" aria-modal="true" aria-labelledby="genic-demo-title"
            aria-describedby="genic-demo-desc">
            <template #header>
                <div class="absolute -top-3 -right-3 z-40">
                    <button @click="handleClose" aria-label="Close demo"
                        class="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
                        <WrSVGIcon name="WrCrossIcon"
                            class="text-white p-1 bg-[#5f4ca4] rounded-full border-2 size-[30px]" />
                    </button>
                </div>
            </template>

            <h2 id="genic-demo-title" class="sr-only">Genic UI Live Demo</h2>
            <p id="genic-demo-desc" class="sr-only">
                Pick a predefined prompt to watch Genic UI render a live, interactive component.
            </p>

            <!-- Genic UI container (same UI + styles as the on-page live demo) -->
            <div
                class="p-px rounded-[20px] bg-[linear-gradient(135.39deg,#bdb1ff_7.01%,#6952ed_93.21%)] dark:bg-[linear-gradient(135.39deg,#3840b5_7.01%,#0b0b26_93.21%)]">
                <div
                    class="relative rounded-[19px] overflow-hidden flex flex-col [background:radial-gradient(120%_50%_at_50%_0%,rgba(124,108,246,0.12)_0%,rgba(124,108,246,0)_60%),#ffffff] dark:[background:radial-gradient(120%_50%_at_50%_0%,rgba(124,108,246,0.16)_0%,rgba(124,108,246,0)_60%),#0a0a28]">
                    <!-- Demo header -->
                    <div
                        class="flex items-center justify-between gap-4 border-b border-[#1E1B4B]/10 dark:border-white/10 px-5 py-3 lg:px-6">
                        <div class="flex items-center gap-3">
                            <p class="font-avant-demi m-0 text-[15px] text-[#1E1B4B] dark:text-[#DFE2F1]">Genic<span
                                    class="text-[#6D28D9] dark:text-[#D0BCFF]">UI</span></p>
                            <span
                                class="rounded-full border border-[rgba(124,108,246,0.35)] px-2.5 py-[3px] text-[10px] tracking-[0.05em] text-[#5a5478] dark:border-white/15 dark:text-[#c9cdeb]">Playground</span>
                        </div>
                        <span
                            class="inline-flex items-center gap-[7px] rounded-full bg-[#027100] px-2 py-1 text-xs font-medium text-[#7FFFCB] before:content-[''] before:size-[7px] before:rounded-full before:bg-[#6ee7b7]">Connected</span>
                    </div>

                    <!-- Predefined prompts (always visible) -->
                    <div
                        class="flex gap-2 overflow-x-auto border-b border-[#1E1B4B]/10 dark:border-white/10 px-5 py-3 lg:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <button v-for="(prompt, i) in prompts" :key="i" type="button"
                            @click="onPromptClick(i)"
                            class="shrink-0 cursor-pointer rounded-full border px-3.5 py-1.5 text-[11px] font-medium transition-[filter,background-color,border-color,color] duration-200"
                            :class="i === activeIndex
                                ? 'border-transparent bg-[linear-gradient(95.99deg,#4753dc_3.69%,#262b7e_96.65%)] text-white'
                                : 'border-[rgba(124,108,246,0.3)] bg-[rgba(237,233,254,0.6)] text-[#5a5478] hover:brightness-[1.05] dark:bg-[rgba(24,31,74,0.5)] dark:text-[#c9cdeb] dark:hover:brightness-[1.15]'">
                            {{ prompt.label }}
                        </button>
                    </div>

                    <!-- Chat -->
                    <div class="flex min-h-[440px] flex-col space-y-3 px-5 py-3 lg:px-6 lg:py-4">
                        <!-- Empty state: no prompt selected yet -->
                        <div v-if="activeIndex === null" class="grid flex-1 place-items-center">
                            <p class="m-0 text-[13px] text-[#5A5478] dark:text-[#8B90B5]">Please select a prompt</p>
                        </div>

                        <!-- Conversation -->
                        <template v-else>
                            <!-- User message (types out) -->
                            <div class="flex items-end justify-end gap-2.5 transition-opacity duration-300"
                                :class="userStarted ? 'opacity-100' : 'opacity-0'">
                                <div class="max-w-[75%] sm:max-w-[60%]">
                                    <p class="mb-1.5 text-right text-[9px] text-[#5A5478] dark:text-[#8B90B5]">10:42 AM</p>
                                    <div
                                        class="min-h-[38px] rounded-[14px_14px_4px_14px] bg-[linear-gradient(135deg,#7c5cfc_0%,#5b54e8_100%)] px-3.5 py-2.5 text-[13px] leading-normal text-white">{{
                                            userText }}</div>
                                </div>
                                <span aria-hidden="true"
                                    class="grid place-items-center flex-shrink-0 size-7 rounded-full text-[8px] font-semibold tracking-[0.05em] text-white bg-[linear-gradient(135deg,#a78bfa,#7c3aed)]">YOU</span>
                            </div>

                            <!-- Agent reply (thinks, then streams) -->
                            <div class="flex items-start gap-3 transition-opacity duration-300"
                                :class="showAgent ? 'opacity-100' : 'opacity-0'">
                                <span aria-hidden="true"
                                    class="grid place-items-center flex-shrink-0 size-8 rounded-full text-xs font-semibold tracking-[0.05em] text-white bg-[linear-gradient(135deg,#4b4fd6,#262b7e)] shadow-[0_0_12px_rgba(124,108,246,0.5)]">A</span>
                                <div class="min-w-0">
                                    <p class="flex flex-wrap items-center gap-x-2 text-[13px] font-medium text-[#1E1B4B] dark:text-white">
                                        Agent
                                        <span class="text-[9px] font-normal tracking-[0.1em] text-[#5A5478] dark:text-[#8B90B5]">{{
                                            agentThinking ? "THINKING" : "STREAM CONTINUED" }}</span>
                                    </p>
                                    <div v-if="agentThinking" class="mt-2 flex items-center gap-1.5" aria-hidden="true">
                                        <span class="genic-demo-dot size-[5px] rounded-full bg-[#6d28d9] dark:bg-[#8b90b5]"></span>
                                        <span class="genic-demo-dot size-[5px] rounded-full bg-[#6d28d9] dark:bg-[#8b90b5]"
                                            style="animation-delay: 0.15s"></span>
                                        <span class="genic-demo-dot size-[5px] rounded-full bg-[#6d28d9] dark:bg-[#8b90b5]"
                                            style="animation-delay: 0.3s"></span>
                                    </div>
                                    <p v-if="agentText"
                                        class="mt-1.5 text-[13px] leading-relaxed text-[#5A5478] dark:text-[#C9CDEB]">
                                        {{ agentText }}<span v-if="typingAgent"
                                            class="genic-demo-caret inline-block w-0.5 h-[1em] ml-0.5 align-text-bottom bg-current"
                                            aria-hidden="true"></span>
                                    </p>
                                    <div class="mt-2.5 flex flex-wrap gap-2 transition-all duration-500"
                                        :class="showChips ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'">
                                        <span
                                            class="rounded-md border border-[rgba(124,108,246,0.35)] bg-[rgba(237,233,254,0.8)] px-2 py-[3px] font-mono text-[10px] text-[#5a5478] dark:bg-[rgba(24,31,74,0.5)] dark:text-[#8b90b5]">{{
                                            demo.chip }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Panel (slides in last) -->
                            <div
                                class="transition-all duration-500 ease-out rounded-[14px] border border-[rgba(124,108,246,0.3)] bg-[rgba(237,233,254,0.6)] px-4 py-3 dark:bg-[rgba(124,108,246,0.07)]"
                                :class="showPanel ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'">
                                <div class="flex flex-wrap items-center justify-between gap-3">
                                    <p class="m-0 text-[13px] font-medium text-[#1E1B4B] dark:text-white">{{
                                        demo.panelTitle }}</p>
                                    <span
                                        class="rounded-full border border-[rgba(124,108,246,0.35)] bg-[rgba(237,233,254,0.8)] px-2.5 py-1 text-[9px] tracking-[0.08em] text-[#5a5478] whitespace-nowrap dark:bg-[rgba(24,31,74,0.5)] dark:text-[#c9cdeb]">{{
                                        demo.panelPill }}</span>
                                </div>

                                <!-- Panel body: table / chart / weather -->
                                <div v-if="demo.panel.type === 'table'" class="mt-3">
                                    <div
                                        class="grid grid-cols-[1fr_auto_auto] gap-x-4 border-b border-[#1E1B4B]/10 dark:border-white/10 pb-2 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">
                                        <p v-for="head in demo.panel.head" :key="head" class="m-0">{{ head }}</p>
                                    </div>
                                    <div v-for="row in demo.panel.rows" :key="row.name"
                                        class="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 border-b border-[#1E1B4B]/10 dark:border-white/5 py-1.5 last:border-0">
                                        <div class="flex min-w-0 items-center gap-2.5">
                                            <span :style="{ background: row.color }"
                                                class="grid place-items-center flex-shrink-0 size-[30px] rounded-full text-[10px] font-semibold text-white"
                                                aria-hidden="true">{{ row.initials }}</span>
                                            <div class="min-w-0">
                                                <p class="m-0 text-[12px] leading-tight text-[#1E1B4B] dark:text-white">{{
                                                    row.name }}</p>
                                                <p class="m-0 text-[10px] leading-tight text-[#5A5478] dark:text-[#8B90B5]">{{
                                                    row.role }}</p>
                                            </div>
                                        </div>
                                        <span
                                            class="rounded-full border border-[rgba(124,108,246,0.4)] bg-[rgba(91,84,232,0.12)] px-3 py-[3px] text-[10px] text-[#4753dc] dark:bg-[rgba(91,84,232,0.25)] dark:text-white">{{
                                            row.tag }}</span>
                                        <span
                                            class="inline-flex items-center gap-[5px] text-[11px] before:content-[''] before:size-1.5 before:rounded-full before:bg-current"
                                            :class="statusColorClasses[row.status]">{{ row.statusLabel }}</span>
                                    </div>
                                </div>

                                <div v-else-if="demo.panel.type === 'chart'" class="mt-3">
                                    <div
                                        class="rounded-[10px] border border-[rgba(124,108,246,0.25)] bg-[rgba(237,233,254,0.6)] dark:bg-[rgba(24,31,74,0.5)] px-3 py-3">
                                        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <!-- Left: chart -->
                                            <div class="flex flex-col justify-center">
                                                <div class="relative cursor-crosshair" @mousemove="onChartMove"
                                                    @mouseleave="onChartLeave">
                                                    <svg viewBox="0 0 320 96" class="h-auto w-full overflow-visible"
                                                        aria-hidden="true">
                                                        <defs>
                                                            <linearGradient id="genic-demo-modal-chart-fill" x1="0"
                                                                y1="0" x2="0" y2="1">
                                                                <stop offset="0%" stop-color="rgba(139,131,255,0.35)" />
                                                                <stop offset="100%" stop-color="rgba(139,131,255,0)" />
                                                            </linearGradient>
                                                        </defs>

                                                        <!-- Gridlines -->
                                                        <line x1="0" x2="320" y1="18" y2="18"
                                                            class="stroke-[rgba(30,27,75,0.1)] dark:stroke-[rgba(255,255,255,0.06)]" />
                                                        <line x1="0" x2="320" y1="44" y2="44"
                                                            class="stroke-[rgba(30,27,75,0.1)] dark:stroke-[rgba(255,255,255,0.06)]" />
                                                        <line x1="0" x2="320" y1="70" y2="70"
                                                            class="stroke-[rgba(30,27,75,0.1)] dark:stroke-[rgba(255,255,255,0.06)]" />

                                                        <!-- Previous period (dashed) -->
                                                        <path
                                                            d="M0,88 C15,87 31,87 46,86 C61,85 77,85 92,84 C107,83 123,82 138,80 C153,78 169,79 184,78 C199,77 215,74 230,72 C245,70 261,71 276,70 C291,69 305,66 320,64"
                                                            fill="none" stroke="rgba(139,144,181,0.5)" stroke-width="1.5"
                                                            stroke-dasharray="4 4" />

                                                        <!-- Area + main line -->
                                                        <path
                                                            d="M0,84 C15,80 31,75 46,73 C61,71 77,79 92,77 C107,75 123,62 138,56 C153,50 169,54 184,51 C199,48 215,38 230,31 C245,24 261,25 276,22 C291,19 305,12 320,7 L320,96 L0,96 Z"
                                                            fill="url(#genic-demo-modal-chart-fill)" />
                                                        <path
                                                            d="M0,84 C15,80 31,75 46,73 C61,71 77,79 92,77 C107,75 123,62 138,56 C153,50 169,54 184,51 C199,48 215,38 230,31 C245,24 261,25 276,22 C291,19 305,12 320,7"
                                                            fill="none" stroke="#8B83FF" stroke-width="2" stroke-linecap="round" />

                                                        <!-- Data markers -->
                                                        <circle v-for="point in chartPoints" :key="point.x" :cx="point.x"
                                                            :cy="point.y" r="2.5" fill="#0A0A28" stroke="#8B83FF"
                                                            stroke-width="1.5" />

                                                        <!-- Endpoint + ping -->
                                                        <circle cx="320" cy="7" r="3" fill="none" stroke="#8B83FF"
                                                            stroke-width="1" class="genic-chart-ping" />
                                                        <circle cx="320" cy="7" r="3" fill="#8B83FF" />

                                                        <!-- Hover crosshair -->
                                                        <g v-if="hoverPoint">
                                                            <line :x1="hoverPoint.x" :x2="hoverPoint.x" y1="0" y2="96"
                                                                stroke="rgba(139,131,255,0.35)" stroke-dasharray="3 3" />
                                                            <circle :cx="hoverPoint.x" :cy="hoverPoint.y" r="3.5"
                                                                fill="#8B83FF" stroke="#0A0A28" stroke-width="1.5" />
                                                        </g>
                                                    </svg>

                                                    <!-- Hover tooltip -->
                                                    <div v-if="hoverPoint"
                                                        class="pointer-events-none absolute z-10 rounded-md border border-[rgba(124,108,246,0.4)] bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(24,31,74,0.95)] px-2 py-1 text-center [transform:translate(-50%,calc(-100%-8px))]"
                                                        :style="{ left: hoverPoint.x / 320 * 100 + '%', top: hoverPoint.y / 96 * 100 + '%' }">
                                                        <p class="m-0 text-[10px] font-medium leading-tight text-[#1E1B4B] dark:text-white">{{
                                                            hoverPoint.value }}</p>
                                                        <p class="m-0 text-[8px] leading-tight text-[#5A5478] dark:text-[#8B90B5]">{{
                                                            hoverPoint.label }}</p>
                                                    </div>
                                                </div>
                                                <!-- X axis -->
                                                <div
                                                    class="mt-2 flex items-center justify-between text-[9px] text-[#5A5478] dark:text-[#8B90B5]">
                                                    <span v-for="month in demo.panel.axis" :key="month">{{ month }}</span>
                                                </div>
                                            </div>

                                            <!-- Right: details -->
                                            <div class="flex flex-col justify-center gap-3">
                                                <!-- Legend -->
                                                <div class="flex items-center gap-4 text-[10px] text-[#5A5478] dark:text-[#8B90B5]">
                                                    <span class="flex items-center gap-1.5">
                                                        <span class="inline-block size-2 rounded-full bg-[#8B83FF]"></span>{{
                                                            demo.panel.legendA || "This week" }}
                                                    </span>
                                                    <span class="flex items-center gap-1.5">
                                                        <span
                                                            class="inline-block w-3 border-t border-dashed border-[#8B90B5]"></span>{{
                                                            demo.panel.legendB || "Last week" }}
                                                    </span>
                                                </div>
                                                <!-- Top holdings -->
                                                <div v-if="demo.panel.holdings && demo.panel.holdings.length">
                                                    <p class="mb-2 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">Top
                                                        Holdings</p>
                                                    <div class="space-y-2">
                                                        <div v-for="holding in demo.panel.holdings" :key="holding.ticker"
                                                            class="flex items-center justify-between rounded-lg border border-[rgba(124,108,246,0.2)] bg-[rgba(237,233,254,0.4)] dark:bg-[rgba(24,31,74,0.4)] px-3 py-1.5">
                                                            <div class="flex items-center gap-2.5">
                                                                <span
                                                                    class="size-7 rounded-full bg-[linear-gradient(135deg,#4b4fd6,#262b7e)] dark:bg-[linear-gradient(135deg,#8b5cf6,#5b21b6)] flex items-center justify-center text-[10px] font-medium text-white">
                                                                    {{ holding.ticker.slice(0, 2) }}</span>
                                                                <div>
                                                                    <p class="m-0 text-[12px] leading-tight text-[#1E1B4B] dark:text-white">{{
                                                                        holding.name }}</p>
                                                                    <p class="m-0 text-[10px] leading-tight text-[#5A5478] dark:text-[#8B90B5]">{{
                                                                        holding.ticker }}</p>
                                                                </div>
                                                            </div>
                                                            <span class="text-[12px] font-medium text-[#1E1B4B] dark:text-white">{{
                                                                holding.allocation }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-else-if="demo.panel.type === 'weather'" class="mt-3">
                                    <!-- Loading -->
                                    <div v-if="weatherLoading"
                                        class="grid h-[160px] place-items-center rounded-[10px] border border-[rgba(124,108,246,0.25)] bg-[rgba(237,233,254,0.5)] dark:bg-[rgba(24,31,74,0.5)]">
                                        <div class="flex items-center gap-2 text-[12px] text-[#5A5478] dark:text-[#8B90B5]">
                                            <span
                                                class="size-3.5 animate-spin rounded-full border-2 border-[#8B83FF] border-t-transparent"></span>
                                            Fetching live weather…
                                        </div>
                                    </div>

                                    <!-- Error -->
                                    <div v-else-if="weatherError"
                                        class="grid h-[160px] place-items-center rounded-[10px] border border-[rgba(124,108,246,0.25)] bg-[rgba(237,233,254,0.5)] dark:bg-[rgba(24,31,74,0.5)]">
                                        <p class="text-[12px] text-[#5A5478] dark:text-[#8B90B5]">Weather is
                                            unavailable right now.</p>
                                    </div>

                                    <!-- Loaded -->
                                    <div v-else-if="weather"
                                        class="rounded-[10px] border border-[rgba(124,108,246,0.25)] bg-[rgba(237,233,254,0.5)] px-4 py-3 dark:bg-[rgba(24,31,74,0.5)]">
                                        <div class="flex items-start justify-between gap-3">
                                            <div class="min-w-0">
                                                <p class="m-0 truncate text-[15px] font-medium text-[#1E1B4B] dark:text-white">{{
                                                    weather.location }}</p>
                                                <p class="m-0 mt-1 flex items-center gap-1.5 text-[12px] text-[#5A5478] dark:text-[#C9CDEB]">
                                                    <span class="text-lg leading-none" aria-hidden="true">{{
                                                        weather.icon }}</span>{{
                                                    weather.condition }}
                                                </p>
                                            </div>
                                            <div class="text-right">
                                                <p class="m-0 text-4xl font-semibold leading-none text-[#1E1B4B] dark:text-white">{{
                                                    weather.temp }}°</p>
                                                <p class="m-0 mt-1 text-[11px] text-[#5A5478] dark:text-[#8B90B5]">Feels
                                                    like {{ weather.feelsLike }}°</p>
                                            </div>
                                        </div>
                                        <div class="mt-4 grid grid-cols-3 gap-2">
                                            <div
                                                class="rounded-[10px] border border-[rgba(124,108,246,0.2)] bg-[rgba(237,233,254,0.5)] px-3 py-1.5 text-center dark:bg-[rgba(24,31,74,0.5)]">
                                                <p class="m-0 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">Humidity</p>
                                                <p class="m-0 mt-1 text-[15px] font-medium text-[#1E1B4B] dark:text-white">{{
                                                    weather.humidity }}%</p>
                                            </div>
                                            <div
                                                class="rounded-[10px] border border-[rgba(124,108,246,0.2)] bg-[rgba(237,233,254,0.5)] px-3 py-1.5 text-center dark:bg-[rgba(24,31,74,0.5)]">
                                                <p class="m-0 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">Wind</p>
                                                <p class="m-0 mt-1 text-[15px] font-medium text-[#1E1B4B] dark:text-white">{{
                                                    weather.wind }} <span class="text-[10px] font-normal">km/h</span></p>
                                            </div>
                                            <div
                                                class="rounded-[10px] border border-[rgba(124,108,246,0.2)] bg-[rgba(237,233,254,0.5)] px-3 py-1.5 text-center dark:bg-[rgba(24,31,74,0.5)]">
                                                <p class="m-0 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">High / Low</p>
                                                <p class="m-0 mt-1 text-[15px] font-medium text-[#1E1B4B] dark:text-white">{{
                                                    weather.high }}° / {{ weather.low }}°</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Apply row -->
                                <div v-if="demo.applyLabel" class="mt-2.5">
                                    <div
                                        class="flex flex-wrap items-center gap-2 rounded-[10px] border border-[rgba(124,108,246,0.25)] bg-[rgba(237,233,254,0.6)] px-3 py-1.5 dark:bg-[rgba(24,31,74,0.5)]"
                                        :class="demo.applyText ? 'justify-between' : 'justify-end'">
                                        <p v-if="demo.applyText" class="m-0 text-[11px] text-[#5A5478] dark:text-[#8B90B5]">{{
                                            demo.applyText
                                            }}</p>
                                        <button type="button"
                                            class="inline-flex items-center gap-1.5 cursor-pointer rounded-lg border-0 bg-[linear-gradient(95.99deg,#4753dc_3.69%,#262b7e_96.65%)] px-3.5 py-[7px] text-[11px] text-white transition-[filter] duration-200 hover:brightness-[1.15] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7782ff]">
                                            {{ demo.applyLabel }}
                                            <WrIcon name="streamline:move-right" class="size-3" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>

                    <!-- Input bar -->
                    <div class="flex items-center gap-3 border-t border-[#1E1B4B]/10 dark:border-white/10 px-5 py-3.5 lg:px-6">
                        <span aria-hidden="true"
                            class="grid place-items-center flex-shrink-0 size-[26px] rounded-full border border-[rgba(124,108,246,0.4)] text-[#5a5478] dark:text-[#8b90b5]">
                            <svg viewBox="0 0 12 12" class="size-3" fill="none" stroke="currentColor" stroke-width="1.5"
                                stroke-linecap="round" aria-hidden="true">
                                <path d="M6 1v10M1 6h10" />
                            </svg>
                        </span>
                        <p class="m-0 flex-1 text-[12px]">
                            <span v-if="inputText || typingInput" class="text-[#1E1B4B] dark:text-[#DFE2F1]">{{ inputText
                                }}<span v-if="typingInput"
                                    class="genic-demo-caret inline-block w-0.5 h-[1em] ml-0.5 align-text-bottom bg-current"
                                    aria-hidden="true"></span></span>
                            <span v-else class="text-[#5A5478] dark:text-[#8B90B5]">Add GenicUI anything or command a
                                component...</span>
                        </p>
                        <button type="button"
                            class="inline-flex items-center gap-1.5 cursor-pointer rounded-lg border-0 bg-[linear-gradient(95.99deg,#4753dc_3.69%,#262b7e_96.65%)] px-3.5 py-2 text-xs text-white transition-[transform_0.15s_ease,filter_0.2s_ease] hover:brightness-[1.15] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7782ff]"
                            :class="sending ? 'scale-[0.94] brightness-[1.25]' : ''">
                            Send
                            <span class="text-[10px] text-white/60" aria-hidden="true">⌘</span>
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    </div>
</template>

<style scoped>
/* Typing caret */
.genic-demo-caret {
    animation: genic-caret-blink 0.8s step-end infinite;
}

@keyframes genic-caret-blink {
    50% {
        opacity: 0;
    }
}

/* Agent thinking dots */
.genic-demo-dot {
    animation: genic-dot-bounce 1s ease-in-out infinite;
}

@keyframes genic-dot-bounce {
    0%,
    80%,
    100% {
        transform: translateY(0);
        opacity: 0.5;
    }

    40% {
        transform: translateY(-3px);
        opacity: 1;
    }
}

/* Endpoint ping */
@keyframes genic-chart-ping {
    0% {
        r: 3;
        opacity: 0.6;
    }

    100% {
        r: 10;
        opacity: 0;
    }
}

.genic-chart-ping {
    animation: genic-chart-ping 1.6s ease-out infinite;
}

/* Reduced motion: no blink/bounce/ping (JS shows the conversation instantly) */
@media (prefers-reduced-motion: reduce) {
    .genic-demo-caret,
    .genic-demo-dot {
        animation: none;
    }

    .genic-chart-ping {
        animation: none;
        opacity: 0;
    }
}
</style>
